import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";

export interface SetupStatus {
  loading: boolean;
  ghlConnected: boolean;
  stageMappingsConfigured: boolean;
  googleAdsConnected: boolean;
  metaAdsConnected: boolean;
  googleAdsNotNeeded: boolean;
  metaAdsNotNeeded: boolean;
  fanbasisConnected: boolean;
  paymentMethodCount: number;
  vhTeamMemberCount: number;
  revShareEligibleNotActive: boolean;
}

const DEFAULT_STATUS: SetupStatus = {
  loading: true,
  ghlConnected: false,
  stageMappingsConfigured: false,
  googleAdsConnected: false,
  metaAdsConnected: false,
  googleAdsNotNeeded: false,
  metaAdsNotNeeded: false,
  fanbasisConnected: false,
  paymentMethodCount: 0,
  vhTeamMemberCount: 0,
  revShareEligibleNotActive: false,
};

export function useSetupStatus(clientId: string | null | undefined): SetupStatus {
  const [status, setStatus] = useState<SetupStatus>(DEFAULT_STATUS);

  useEffect(() => {
    if (!clientId) {
      setStatus({ ...DEFAULT_STATUS, loading: false });
      return;
    }

    let cancelled = false;

    const fetchStatus = async () => {
      try {
        // Run all queries in parallel — each returns { data, error, count }
        // None of these throw; errors are in the .error field
        const [
          integrationsResult,
          stageMappingsResult,
          clientResult,
          paymentMethodsResult,
          vhTeamResult,
        ] = await Promise.all([
          supabase
            .from("integrations")
            .select("provider")
            .eq("client_id", clientId)
            .eq("status", "active"),
          supabase
            .from("stage_mappings")
            .select("id", { count: "exact", head: true })
            .eq("client_id", clientId),
          // Use .maybeSingle() instead of .single() to avoid error when no row exists
          supabase
            .from("clients")
            .select("fanbasis_customer_id, vh_attributed_lifetime_cents, rev_share_active, integrations_not_needed")
            .eq("id", clientId)
            .maybeSingle(),
          supabase
            .from("payment_methods")
            .select("id", { count: "exact", head: true })
            .eq("client_id", clientId)
            .eq("status", "active"),
          supabase
            .from("vh_team_members")
            .select("id", { count: "exact", head: true })
            .eq("status", "active"),
        ]);

        if (cancelled) return;

        const providers = (integrationsResult.data || []).map((i: { provider: string }) => i.provider);
        const client = clientResult.data;
        const lifetimeCents = client?.vh_attributed_lifetime_cents || 0;
        const notNeeded: string[] = (client?.integrations_not_needed as string[]) || [];

        setStatus({
          loading: false,
          ghlConnected: providers.includes("ghl"),
          googleAdsConnected: providers.includes("google_ads"),
          metaAdsConnected: providers.includes("meta_ads"),
          googleAdsNotNeeded: notNeeded.includes("google_ads"),
          metaAdsNotNeeded: notNeeded.includes("meta_ads"),
          stageMappingsConfigured: (stageMappingsResult.count || 0) > 0,
          fanbasisConnected: !!client?.fanbasis_customer_id,
          paymentMethodCount: paymentMethodsResult.count || 0,
          vhTeamMemberCount: vhTeamResult.count || 0,
          revShareEligibleNotActive: lifetimeCents >= 5000000 && !client?.rev_share_active,
        });
      } catch (err) {
        console.error("Failed to fetch setup status:", err);
        if (!cancelled) {
          setStatus({ ...DEFAULT_STATUS, loading: false });
        }
      }
    };

    fetchStatus();
    return () => { cancelled = true; };
  }, [clientId]);

  return status;
}
